import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { RcmParsed } from "../lib/rcmParser";
import { parseRcmFile } from "../lib/rcmParser";
import type { WorkStepCandidate, WorkStepMapping } from "../lib/workStepImport";
import {
  buildWorkStepCandidates,
  suggestWorkStepMapping,
} from "../lib/workStepImport";

type WorkStepImportContextValue = {
  parsed: RcmParsed | null;
  fileName: string;
  mapping: WorkStepMapping;
  setFileAndParse: (file: File) => Promise<void>;
  candidates: WorkStepCandidate[];
  clear: () => void;
  loading: boolean;
};

const defaultValue: WorkStepImportContextValue = {
  parsed: null,
  fileName: "",
  mapping: {},
  setFileAndParse: async () => {},
  candidates: [],
  clear: () => {},
  loading: false,
};

const SESSION_KEY = "workStepImportState:v1";

const WorkStepImportContext =
  createContext<WorkStepImportContextValue>(defaultValue);

type Persisted = {
  parsed: RcmParsed | null;
  fileName?: string;
  mapping: WorkStepMapping;
};

function loadPersisted(): Persisted | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function persist(state: Persisted) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(state));
  } catch {
    // ignore storage errors (quota, etc.)
  }
}

export const WorkStepImportProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [parsed, setParsed] = useState<RcmParsed | null>(null);
  const [fileName, setFileName] = useState<string>("");
  const [mapping, setMappingState] = useState<WorkStepMapping>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const persisted = loadPersisted();
    if (persisted) {
      setParsed(persisted.parsed);
      setFileName(persisted.fileName || "");
      setMappingState(persisted.mapping || {});
    }
  }, []);

  useEffect(() => {
    persist({ parsed, fileName, mapping });
  }, [parsed, fileName, mapping]);

  const setFileAndParse = useCallback(async (file: File) => {
    setLoading(true);
    try {
      const nextParsed = await parseRcmFile(file);
      setParsed(nextParsed);
      setFileName(file.name);
      // Hardcoded product-knowledge mapping, applied silently — no
      // mapping UI (user decision, ticket 05).
      setMappingState(suggestWorkStepMapping(nextParsed.headers || []));
    } finally {
      setLoading(false);
    }
  }, []);

  const candidates = useMemo(() => {
    if (!parsed) return [];
    return buildWorkStepCandidates(parsed, mapping);
  }, [parsed, mapping]);

  const clear = useCallback(() => {
    setParsed(null);
    setFileName("");
    setMappingState({});
    try {
      sessionStorage.removeItem(SESSION_KEY);
    } catch {
      console.error("Error clearing work step import context");
    }
  }, []);

  const value: WorkStepImportContextValue = {
    parsed,
    fileName,
    mapping,
    setFileAndParse,
    candidates,
    clear,
    loading,
  };

  return (
    <WorkStepImportContext.Provider value={value}>
      {children}
    </WorkStepImportContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useWorkStepImport = () => useContext(WorkStepImportContext);
